import axios from "axios";
import { notification, message } from "antd";
import { ZharpLogger } from "@zharp/logs";
import { ZharpAPM } from "@zharp/apm";
import { ZHARP_API_KEY, REACT_APP_NODE_ENV, REACT_APP_VERSION } from "./config";

let initialized = false;

// Only plain strings are forwarded; intl elements and JSX are dropped.
const textOf = (value) => (typeof value === "string" ? value : undefined);

// Every axios call in the app goes through the default instance, so one
// interceptor covers the messaging, people, content and SSO APIs. Request
// bodies, query strings and headers are left out (tokens, phone numbers).
const reportAxiosErrors = () => {
  axios.interceptors.response.use(undefined, (error) => {
    if (axios.isCancel(error)) return Promise.reject(error);
    const config = error.config || {};
    const response = error.response;
    const httpStatus = response ? response.status : undefined;
    const path = String(config.url || "").split("?")[0];
    const metadata = {
      source: "http",
      method: (config.method || "get").toUpperCase(),
      baseURL: config.baseURL,
      url: path,
      httpStatus,
      serverMessage:
        response && response.data ? textOf(response.data.message) : undefined,
      error: error.message,
    };
    const log = !httpStatus || httpStatus >= 500 ? logError : logWarn;
    log(`${metadata.method} ${config.baseURL || ""}${path} failed`, metadata);
    return Promise.reject(error);
  });
};

// Error toasts are what the user actually saw, so record each one.
const reportErrorToasts = () => {
  const showNotification = notification.error;
  notification.error = (config = {}) => {
    logWarn(textOf(config.message) || "Error notification shown", {
      source: "ui-notification",
      description: textOf(config.description),
    });
    return showNotification(config);
  };

  const showMessage = message.error;
  message.error = (content, ...rest) => {
    const text =
      textOf(content) || (content ? textOf(content.content) : undefined);
    logWarn(text || "Error message shown", { source: "ui-message" });
    return showMessage(content, ...rest);
  };
};

export const initZharp = () => {
  if (initialized || !ZHARP_API_KEY || typeof window === "undefined") return;
  initialized = true;
  ZharpLogger.init({
    apiKey: ZHARP_API_KEY,
    service: "push-web-zharp",
    environment: REACT_APP_NODE_ENV,
    appVersion: REACT_APP_VERSION,
    // window.onerror and unhandled rejections are captured by default
    captureConsole: ["warn", "error"],
  });
  // Reuses the logger's settings. Replay stays off (Amplitude already records
  // sessions) and traceparent is only sent same-origin until the APIs allow
  // that header in CORS.
  ZharpAPM.init({ ignore: [/amplitude\.com/, /intercom/] });
  reportAxiosErrors();
  reportErrorToasts();
};

export const logError = (message, metadata) => {
  if (initialized) ZharpLogger.error(message, metadata);
};

export const logWarn = (message, metadata) => {
  if (initialized) ZharpLogger.warn(message, metadata);
};

export default ZharpLogger;
